/**
 * Pure ordering + selection helpers for the GF switcher. The edge already
 * returns the list most-recently-active first and hides `disabled` companions,
 * but the client re-derives both defensively (and it is the unit-tested contract
 * the doc pins: "switcher orders by `updated_at`"). No Solid, no DOM.
 */
import type { CompanionView } from '../api/types.ts';

/** Epoch ms of a companion's `updatedAt`, or `0` when unparseable (sorts last). */
function recency(companion: CompanionView): number {
  const time = Date.parse(companion.updatedAt);
  return Number.isNaN(time) ? 0 : time;
}

/**
 * Sort a copy most-recently-active first (`updated_at` descending). Ties break on
 * `id` so the order is total and stable across renders — never a flickering list.
 */
export function sortByRecency(list: readonly CompanionView[]): CompanionView[] {
  return [...list].sort((a, b) => recency(b) - recency(a) || a.id.localeCompare(b.id));
}

/** The switcher's companions — `disabled` hidden, most-recent-first. */
export function visibleCompanions(list: readonly CompanionView[]): CompanionView[] {
  return sortByRecency(list.filter((companion) => companion.status !== 'disabled'));
}

/**
 * Resolve the active companion from the route: the requested id when it is a
 * visible companion, else the most-recently-active one, else `null` (empty
 * account). Keeps the swap resilient to a stale deep link to a disabled/removed GF.
 */
export function resolveActive(
  list: readonly CompanionView[],
  requestedId: string | null,
): CompanionView | null {
  const visible = visibleCompanions(list);
  if (requestedId) {
    const match = visible.find((companion) => companion.id === requestedId);
    if (match) {
      return match;
    }
  }
  return visible[0] ?? null;
}
